// src/features/inventory/pages/SupplierListPage.jsx
import React, { useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { useSuppliers } from '../hooks/useSuppliers';
import { useInventorySupplierPermissions } from '../../auth/hooks/usePermissions';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';

import PageHeader from '../../../components/common/PageHeader';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

export default function SupplierListPage() {
  const { t } = useTranslation(['inventory', 'common']);
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const { canCreate, canUpdate, canDelete } = useInventorySupplierPermissions();

  const {
    suppliers,
    loading,
    error,
    clearError,
    fetchSuppliers,
    updateSupplier,
    deleteSupplier,
  } = useSuppliers();

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const handleToggleActive = useCallback(async (e, row) => {
    e.stopPropagation();
    if (!canUpdate) return;
    const result = await updateSupplier(row.supplier_id, { active: !row.active });
    if (!result.success) showError(result.error);
  }, [canUpdate, updateSupplier, showError]);

  const handleDelete = useCallback((e, row) => {
    e.stopPropagation();
    showConfirmDialog({
      title: t('inventory:suppliers.confirm.delete_title'),
      message: t('inventory:suppliers.confirm.delete_message', { name: row.name }),
      severity: 'error',
      onConfirm: async () => {
        const result = await deleteSupplier(row.supplier_id);
        if (result.success) {
          showSuccess(t('inventory:suppliers.success.deleted'));
        } else {
          showError(result.error);
        }
      },
    });
  }, [showConfirmDialog, deleteSupplier, showSuccess, showError, t]);

  const columns = useMemo(() => [
    {
      field: 'code',
      headerName: t('inventory:suppliers.grid.code'),
      width: 120,
      renderCell: (params) => (
        <Chip label={params.value} size="small" variant="outlined" color="info" />
      ),
    },
    {
      field: 'name',
      headerName: t('inventory:suppliers.grid.name'),
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'contact_email',
      headerName: t('inventory:suppliers.grid.email'),
      flex: 1,
      minWidth: 160,
      renderCell: (params) =>
        params.value
          ? <Typography variant="body2">{params.value}</Typography>
          : <Typography variant="body2" color="text.secondary">—</Typography>,
    },
    {
      field: 'contact_phone',
      headerName: t('inventory:suppliers.grid.phone'),
      width: 140,
      renderCell: (params) =>
        params.value
          ? <Typography variant="body2">{params.value}</Typography>
          : <Typography variant="body2" color="text.secondary">—</Typography>,
    },
    {
      field: 'active',
      headerName: t('inventory:suppliers.grid.active'),
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.row.active ? t('common:status.active') : t('common:status.inactive')}
          color={params.row.active ? 'success' : 'default'}
          size="small"
          onClick={canUpdate ? (e) => handleToggleActive(e, params.row) : undefined}
          sx={canUpdate ? { cursor: 'pointer' } : undefined}
        />
      ),
    },
    {
      field: 'actions',
      renderHeader: () => <></>,
      width: 52,
      sortable: false,
      filterable: false,
      renderCell: (params) => canDelete ? (
        <IconButton
          size="small"
          onClick={(e) => handleDelete(e, params.row)}
          title={t('common:buttons.delete')}
          color="error"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      ) : null,
    },
  ], [t, canUpdate, canDelete, handleToggleActive, handleDelete]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <PageHeader
          icon={LocalShippingIcon}
          title={t('inventory:suppliers.title')}
          subtitle={t('inventory:suppliers.subtitle')}
          actions={<>
            <Tooltip title={t('common:buttons.refresh')}>
              <IconButton onClick={fetchSuppliers}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate('create')}
              >
                {t('inventory:suppliers.buttons.add')}
              </Button>
            )}
          </>}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
            {error}
          </Alert>
        )}

        <DataGrid
          rows={suppliers}
          columns={columns}
          getRowId={(row) => row.supplier_id}
          getRowClassName={(params) => !params.row.active ? 'row-inactive' : ''}
          loading={loading}
          onRowClick={(params) => navigate(`${params.row.supplier_id}`)}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          disableColumnFilter
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          sx={{
            '& .row-inactive': { opacity: 0.5 },
            '& .MuiDataGrid-row': { cursor: 'pointer' },
            '& .MuiDataGrid-cell': {
              minHeight: 'unset !important',
              maxHeight: 'none !important',
              height: 'auto !important',
              display: 'flex !important',
              alignItems: 'center !important',
              padding: '8px 16px !important',
            },
            minHeight: 400,
          }}
        />
      </Box>

      <ConfirmDialog />
    </Box>
  );
}
